import { useState, useRef, useEffect } from "react";
import { t } from "../i18n.js";
import { getName } from "../profile.js";
import { ensureCtx, out } from "../audio.js";
import PrizePicker from "../PrizePicker.jsx";
import { submitHammerScore, fetchHammerBoard } from "../hammerboard.js";

/* ==================== STYRKEPRØVEN – high striker ====================
   Tre SEKVENTIELLE faser med ét tap hver: løft (300) -> kraft (300) ->
   sigte (400), max 1000. Meteret har FARVEINDIKATOR (rød/orange/grøn —
   grønt felt er bedst, Daniels ønske). Maskotten svinger en træhammer
   mod pladen, og pucken flyver op ad tårnet ∝ totalen: ≥900 rammer den
   KLOKKEN (ring + konfetti + jubel). Præmie efter score: 500=lille,
   750=mellem, 900=stor — gevinst åbner PrizePicker DIREKTE (kan ikke
   klikkes forbi), opsummeringen vises bagefter (Andedam-læren).
   Poser: 6 renders (gen_plush_gemini --hammer) m. hangtime-sprite +
   TEGNET hammer som fallback, præcis som Slå sækkens pose-system.
===================================================================== */

const W = 360, H = 540;
const FLOOR = 424;
const PANEL_TOP = H - 78;
/* tårnet i højre side */
const TOWER = { x: 292, railW: 22, top: 108, padY: 398 };
const BELL_Y = 96;
const PHASES = ["pull", "power", "aim"];
const WEIGHTS = { pull: 300, power: 300, aim: 400 };
const TUNE = {
  pull:  { perfect: 0.74, tol: 0.30, freq: 1.05 },
  power: { perfect: 0.86, tol: 0.24, freq: 1.65 },
  aim:   { perfect: 0.50, tol: 0.20, freq: 1.45 },
};
const BELL_SCORE = 900;
/* slag-tempo (sat OP 17/7 — "man kan ikke se selve slå-sekvensen"):
   udfaldet tager SWING_T, og strike-posen HOLDES ved pladen i HOLD_T */
const SWING_T = 0.55, HOLD_T = 0.4;
/* KUN én præmie (Daniels regel): STOR bamse ved klokken (≥900) — resten er
   high score-jagt (dobbelt high score som Slå sækken) */
const PRIZE = [[900, "stor"]];
const BEST_KEY = "carnival.hammer";

const loadBest = () => {
  try { return JSON.parse(localStorage.getItem(BEST_KEY) || "{}").best || 0; }
  catch { return 0; }
};
const saveBest = (best) => {
  try { localStorage.setItem(BEST_KEY, JSON.stringify({ best })); } catch {}
};

function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    lock() { const c = ensure(); if (c) tone(760, c.currentTime, 0.07, "square", 0.09); },
    tick(i) { const c = ensure(); if (c) tone(420 + i * 75, c.currentTime, 0.06, "square", 0.07); },
    whoosh() {
      const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(140, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(620, c.currentTime + 0.2);
      g.gain.setValueAtTime(0.07, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.24);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.26);
    },
    thud(p) {
      const c = ensure(); if (!c) return;
      tone(85 + p * 45, c.currentTime, 0.3, "square", 0.16 + p * 0.06);
      tone(52, c.currentTime + 0.02, 0.36, "sine", 0.14);
    },
    rise() {
      const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(300, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(1400, c.currentTime + 0.7);
      g.gain.setValueAtTime(0.07, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.8);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.85);
    },
    bell() {
      const c = ensure(); if (!c) return;
      [1568, 1975, 1568, 2093].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.5, "triangle", 0.15));
      tone(784, c.currentTime, 0.8, "sine", 0.1);
    },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.12, 0.26, "triangle", 0.13));
    },
  };
}

const zoneScore = (u, tune) => Math.max(0, 1 - Math.abs(u - tune.perfect) / tune.tol);
const prizeFor = (total) => (PRIZE.find(([min]) => total >= min) || [0, null])[1];

export default function Hammer({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | result
  const [result, setResult] = useState(null);    // {scores, total, bell, size}
  const [picking, setPicking] = useState(false);
  const [best, setBest] = useState(loadBest);
  const [copied, setCopied] = useState(false);
  /* dobbelt high score (RTDB, punchboard-mønstret) */
  const [hsOpen, setHsOpen] = useState(false);
  const [hsTab, setHsTab] = useState("month");
  const [hsData, setHsData] = useState(null);
  const [hsErr, setHsErr] = useState(false);
  const openHs = () => {
    setHsOpen(true);
    setHsErr(false);
    setHsData(null);
    fetchHammerBoard().then(setHsData).catch(() => setHsErr(true));
  };
  const fmtDate = (ts, withYear) => {
    let lang;
    try { lang = localStorage.getItem("carnival.lang") || undefined; } catch {}
    try {
      return new Date(ts).toLocaleDateString(lang, {
        day: "numeric", month: "short", ...(withYear ? { year: "2-digit" } : {}),
      });
    } catch { return ""; }
  };
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  /* challenge-link: ?g=hammer&ch=<score>&by=<navn> (Slå sækken-mønstret) */
  const chal = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const ch = parseInt(q.get("ch"), 10);
      const by = (q.get("by") || "").slice(0, 12);
      return Number.isFinite(ch) && ch > 0 && ch <= 1000 && by ? { ch, by } : null;
    } catch { return null; }
  })();

  const share = async (total) => {
    const url = `${window.location.origin}${window.location.pathname}?g=hammer&ch=${total}&by=${encodeURIComponent(getName() || "?")}`;
    const text = t("hm_share_text", { n: total });
    try {
      if (navigator.share) { await navigator.share({ title: "Teddy Chase", text, url }); return; }
    } catch { /* deltes ikke — prøv clipboard */ }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  const startGame = () => {
    setResult(null);
    setPicking(false);
    setCopied(false);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const g = {
      idx: 0, t: 0, u: 0,
      locked: {}, scores: {},
      freeze: 0,
      swingT: -1,            // >=0: hammeren svinger + puck-sekvens
      impactDone: false,
      total: 0, bell: false,
      puckY: TOWER.padY - 8, puckVis: false, puckPhase: "idle", puckT: 0,
      litMax: 0, pulses: new Array(12).fill(0),   // taendte segmenter + puls
      bellWob: 0,
      parts: [], popups: [],
      over: false, now: 0,
    };
    if (localStorage.getItem("carnival.debug")) window.__hm = g;

    /* segment-geometri (deles af bake, step og glød-overlay) */
    const segYAt = (s) => TOWER.padY - 16 - (s / 11) * (TOWER.padY - TOWER.top - 30);
    const segGlow = (s) => {
      const k = s / 11;
      return k < 0.45 ? ["#ff8a76", "#e05545"] : k < 0.78 ? ["#ffd08a", "#f0a44e"] : ["#9aff9c", "#5fca62"];
    };

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* TRE poser — én pr. fase (Daniels valg; gen_plush_gemini --hammer) m.
       fallback til hangtime-spriten. Sving/jubel genbruger faserne:
       sving = aim (m. rotation), jubel = lift (m. hoppe-anim). */
    const teddy = new Image();
    teddy.src = "/images/hangtime-teddy.png";
    const poses = {};
    for (const k of ["stand", "lift", "charge", "aim", "strike", "impact"]) {
      const im = new Image();
      // ?v=2: poserne blev udskiftet PÅ PLADS (kædet generering m. ens hammer)
      // — bump ved hver ny pose-generation, ellers hænger 1-times billedcachen
      im.src = `/images/hammer/hammer_${k}.png?v=2`;
      poses[k] = im;
    }
    const poseReady = (k) => poses[k] && poses[k].complete && poses[k].naturalWidth > 0;
    const currentPose = () => {
      // slaget i TO poser: strike = nedsvinget, impact = hammeren I pladen
      if (g.swingT >= 0 && g.swingT < SWING_T) return poseReady("strike") ? "strike" : "aim";
      if (g.swingT >= SWING_T && g.swingT < SWING_T + HOLD_T) {
        if (poseReady("impact")) return "impact";
        return poseReady("strike") ? "strike" : "aim";
      }
      if (g.swingT >= SWING_T + HOLD_T) return g.bell ? "lift" : "charge";
      const key = PHASES[g.idx];
      // sekvensens udgangspunkt: hammeren NEDE (stand) mens løftet times;
      // lift-posen er RESULTATET af låsningen (vises i freeze-pausen)
      if (key === "pull") return poseReady("stand") ? "stand" : "lift";
      if (key === "power") return g.freeze > 0 ? "lift" : "charge";
      return "aim";
    };

    /* ---- statisk baggrund (Gemini-mockup 17/7): fløjlsgardiner, damask-
       bagtæppe m. bokeh + forlystelses-silhuetter, plankegulv m. messing,
       ståltårn m. glas-segmenter, kuppelklokke m. slagarm, buledent plade ---- */
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      const rnd = (i) => { const v = Math.sin(i * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
      // dybt grønt bagtæppe
      const sky = b.createLinearGradient(0, 0, 0, FLOOR);
      sky.addColorStop(0, "#123528");
      sky.addColorStop(0.6, "#0e2a1f");
      sky.addColorStop(1, "#0a1f17");
      b.fillStyle = sky; b.fillRect(0, 0, W, FLOOR);
      // svage damask-hvirvler
      b.strokeStyle = "rgba(140,200,160,0.05)"; b.lineWidth = 6;
      for (let i = 0; i < 5; i++) {
        const cx2 = 60 + rnd(i * 3) * 240, cy2 = 60 + rnd(i * 7) * 180, r = 22 + rnd(i * 11) * 26;
        b.beginPath(); b.arc(cx2, cy2, r, rnd(i) * 3, rnd(i) * 3 + 4.2); b.stroke();
        b.beginPath(); b.arc(cx2 + r * 0.8, cy2 + 6, r * 0.55, rnd(i * 5) * 3, rnd(i * 5) * 3 + 3.6); b.stroke();
      }
      b.lineWidth = 1;
      // bokeh-lys
      for (let i = 0; i < 16; i++) {
        const f = rnd(i * 91), f2 = rnd(i * 53);
        b.fillStyle = `rgba(220,240,190,${0.04 + f2 * 0.05})`;
        b.beginPath(); b.arc(20 + f * (W - 40), 90 + f2 * 240, 4 + f * 9, 0, Math.PI * 2); b.fill();
      }
      // forlystelses-silhuetter (pariserhjul + rutsjebane) nederst i bagtæppet
      b.fillStyle = "rgba(4,16,10,0.55)";
      b.strokeStyle = "rgba(4,16,10,0.55)";
      const fw = { x: 92, y: FLOOR - 78, r: 34 };
      b.lineWidth = 3;
      b.beginPath(); b.arc(fw.x, fw.y, fw.r, 0, Math.PI * 2); b.stroke();
      b.lineWidth = 1.8;
      for (let s = 0; s < 8; s++) {
        const a = (s / 8) * Math.PI * 2;
        b.beginPath(); b.moveTo(fw.x, fw.y);
        b.lineTo(fw.x + Math.cos(a) * fw.r, fw.y + Math.sin(a) * fw.r); b.stroke();
        b.beginPath(); b.arc(fw.x + Math.cos(a) * fw.r, fw.y + Math.sin(a) * fw.r, 3, 0, Math.PI * 2); b.fill();
      }
      b.beginPath(); b.moveTo(fw.x - 16, FLOOR); b.lineTo(fw.x, fw.y); b.lineTo(fw.x + 16, FLOOR); b.stroke();
      b.beginPath();
      b.moveTo(150, FLOOR);
      for (let x = 150; x <= 335; x += 5) {
        const y = FLOOR - 26 - Math.abs(Math.sin((x - 150) * 0.045)) * 34;
        b.lineTo(x, y);
      }
      b.lineTo(335, FLOOR); b.closePath(); b.fill();
      b.lineWidth = 1;
      // GULV: planker m. tonevariation + messing-detaljer
      for (let i = 0; i < 4; i++) {
        const y = FLOOR + i * ((PANEL_TOP - FLOOR) / 4);
        const tone = 0.85 + rnd(i * 13) * 0.3;
        b.fillStyle = `rgb(${Math.round(110 * tone)},${Math.round(72 * tone)},${Math.round(38 * tone)})`;
        b.fillRect(0, y, W, (PANEL_TOP - FLOOR) / 4 + 1);
        b.fillStyle = "rgba(30,15,5,0.4)";
        b.fillRect(0, y, W, 1.6);
        for (let k = 0; k < 3; k++) {
          b.fillStyle = "rgba(30,15,5,0.3)";
          b.fillRect(40 + rnd(i * 7 + k) * 280, y + 2, 1.2, (PANEL_TOP - FLOOR) / 4 - 3);
        }
      }
      b.fillStyle = "rgba(255,235,200,0.25)"; b.fillRect(0, FLOOR, W, 1.6);
      for (const [mx2, my2] of [[36, FLOOR + 26], [206, FLOOR + 12], [330, FLOOR + 30]]) {
        b.fillStyle = "#c9a24b";
        b.beginPath(); b.arc(mx2, my2, 3.2, 0, Math.PI * 2); b.fill();
        b.fillStyle = "rgba(120,80,10,0.8)";
        b.fillRect(mx2 - 2, my2 - 0.7, 4, 1.4);
      }
      // FLØJLSGARDINER i begge sider (fald + folder + sheen)
      for (const side of [0, 1]) {
        const x0 = side === 0 ? 0 : W - 58;
        const grad = b.createLinearGradient(x0, 0, x0 + 58, 0);
        if (side === 0) { grad.addColorStop(0, "#0d3a26"); grad.addColorStop(1, "#082217"); }
        else { grad.addColorStop(0, "#082217"); grad.addColorStop(1, "#0d3a26"); }
        b.fillStyle = grad;
        b.beginPath();
        b.moveTo(x0, 0); b.lineTo(x0 + 58, 0);
        b.quadraticCurveTo(x0 + (side === 0 ? 44 : 14), FLOOR * 0.5, x0 + (side === 0 ? 52 : 6), FLOOR + 8);
        b.lineTo(x0 + (side === 0 ? 0 : 58), FLOOR + 8);
        b.closePath(); b.fill();
        for (let f2 = 0; f2 < 4; f2++) {
          const fx = x0 + (side === 0 ? 8 + f2 * 12 : 50 - f2 * 12);
          b.strokeStyle = f2 % 2 ? "rgba(30,90,60,0.5)" : "rgba(6,20,12,0.6)";
          b.lineWidth = 3.5;
          b.beginPath();
          b.moveTo(fx, 0);
          b.quadraticCurveTo(fx + (side === 0 ? 8 : -8), FLOOR * 0.55, fx + (side === 0 ? 3 : -3), FLOOR + 4);
          b.stroke();
        }
        b.lineWidth = 1;
      }
      // meter-panelet: trae m. guldkant
      const pl = b.createLinearGradient(0, PANEL_TOP, 0, H);
      pl.addColorStop(0, "#7a4e2c"); pl.addColorStop(0.5, "#5f3d1f"); pl.addColorStop(1, "#3f2812");
      b.fillStyle = pl; b.fillRect(0, PANEL_TOP, W, H - PANEL_TOP);
      b.fillStyle = "#c9a24b"; b.fillRect(0, PANEL_TOP, W, 2.5);
      // lille ornament-diamant midt paa skillelinjen
      b.fillStyle = "#e8c15a";
      b.beginPath();
      b.moveTo(W / 2, PANEL_TOP - 5); b.lineTo(W / 2 + 5, PANEL_TOP);
      b.lineTo(W / 2, PANEL_TOP + 5); b.lineTo(W / 2 - 5, PANEL_TOP);
      b.closePath(); b.fill();
      // TÅRNET: staal-skinne m. glas-segmenter (roed nederst -> groen oeverst)
      const tx = TOWER.x, rw = TOWER.railW;
      // fod: METALBOKS der staar paa plankegulvet (topflade + front) m. nitter + BULEDENT slagflade
      const baseW = 104;
      const boxTop = TOWER.padY - 2, boxMid = TOWER.padY + 16, boxBot = FLOOR + 14;
      // topflade (let perspektiv)
      const bp2 = b.createLinearGradient(0, boxTop, 0, boxMid);
      bp2.addColorStop(0, "#8a8f96"); bp2.addColorStop(1, "#5a616a");
      b.fillStyle = bp2;
      b.beginPath();
      b.moveTo(tx - baseW / 2 + 7, boxTop);
      b.lineTo(tx + baseW / 2 - 7, boxTop);
      b.lineTo(tx + baseW / 2, boxMid);
      b.lineTo(tx - baseW / 2, boxMid);
      b.closePath(); b.fill();
      b.strokeStyle = "#c9a24b"; b.lineWidth = 1.6; b.stroke();
      // frontflade ned paa gulvet
      const bpf = b.createLinearGradient(0, boxMid, 0, boxBot);
      bpf.addColorStop(0, "#4d545c"); bpf.addColorStop(0.55, "#3a4149"); bpf.addColorStop(1, "#262c33");
      b.fillStyle = bpf;
      b.fillRect(tx - baseW / 2, boxMid, baseW, boxBot - boxMid);
      b.strokeStyle = "#c9a24b"; b.lineWidth = 1.4;
      b.strokeRect(tx - baseW / 2, boxMid, baseW, boxBot - boxMid);
      // bloed skygge paa plankerne under boksen
      const shd = b.createLinearGradient(0, boxBot, 0, boxBot + 7);
      shd.addColorStop(0, "rgba(10,14,10,0.35)"); shd.addColorStop(1, "rgba(10,14,10,0)");
      b.fillStyle = shd; b.fillRect(tx - baseW / 2 - 6, boxBot + 1, baseW + 12, 7);
      // nitter paa top og front
      for (const [nx2, ny2] of [
        [tx - baseW / 2 + 12, boxTop + 4], [tx + baseW / 2 - 12, boxTop + 4],
        [tx - baseW / 2 + 7, boxMid + 7], [tx + baseW / 2 - 7, boxMid + 7],
        [tx - baseW / 2 + 7, boxBot - 6], [tx + baseW / 2 - 7, boxBot - 6],
      ]) {
        b.fillStyle = "#2c3238";
        b.beginPath(); b.arc(nx2, ny2, 1.8, 0, Math.PI * 2); b.fill();
        b.fillStyle = "rgba(255,255,255,0.25)";
        b.beginPath(); b.arc(nx2 - 0.5, ny2 - 0.6, 0.7, 0, Math.PI * 2); b.fill();
      }
      // dent (krater + revner) hvor hammeren rammer — paa topfladen
      const dent = b.createRadialGradient(tx - 12, TOWER.padY + 6, 1, tx - 10, TOWER.padY + 7, 13);
      dent.addColorStop(0, "#20262c"); dent.addColorStop(0.7, "#3a424a"); dent.addColorStop(1, "rgba(58,66,74,0)");
      b.fillStyle = dent;
      b.beginPath(); b.ellipse(tx - 10, TOWER.padY + 7, 14, 7, 0, 0, Math.PI * 2); b.fill();
      b.strokeStyle = "rgba(25,30,36,0.8)"; b.lineWidth = 1.2;
      for (let s = 0; s < 5; s++) {
        const a = rnd(s * 17) * Math.PI * 2;
        b.beginPath(); b.moveTo(tx - 10, TOWER.padY + 7);
        b.lineTo(tx - 10 + Math.cos(a) * (10 + rnd(s) * 8), TOWER.padY + 7 + Math.sin(a) * (4 + rnd(s * 3) * 4));
        b.stroke();
      }
      b.lineWidth = 1;
      // skinnen (staal m. side-vanger)
      const rail = b.createLinearGradient(tx - rw / 2 - 5, 0, tx + rw / 2 + 5, 0);
      rail.addColorStop(0, "#3a424a"); rail.addColorStop(0.5, "#585f66"); rail.addColorStop(1, "#2c3238");
      b.fillStyle = rail;
      b.fillRect(tx - rw / 2 - 5, TOWER.top - 4, rw + 10, TOWER.padY - TOWER.top + 6);
      b.fillStyle = "#20262c";
      b.fillRect(tx - rw / 2, TOWER.top, rw, TOWER.padY - TOWER.top);
      // glas-segmenter m. messing-skillelinjer; nederste sektion i guldramme
      const SEGS = 12;
      const colFor = (k) => k < 0.45 ? ["#e05545", "#8a1f16"] : k < 0.78 ? ["#f0a44e", "#9a5510"] : ["#5fca62", "#1e6a28"];
      for (let s = 0; s < SEGS; s++) {
        const k = s / (SEGS - 1);
        const y = TOWER.padY - 16 - k * (TOWER.padY - TOWER.top - 30);
        const [c1, c2] = colFor(k);
        const seg = b.createLinearGradient(0, y - 6, 0, y + 6);
        seg.addColorStop(0, c1); seg.addColorStop(1, c2);
        b.fillStyle = seg;
        b.beginPath();
        b.roundRect ? b.roundRect(tx - rw / 2 + 2.5, y - 5.5, rw - 5, 11, 3) : b.rect(tx - rw / 2 + 2.5, y - 5.5, rw - 5, 11);
        b.fill();
        b.fillStyle = "rgba(255,255,255,0.35)";
        b.fillRect(tx - rw / 2 + 4, y - 4.5, rw - 8, 2);
        b.strokeStyle = "rgba(201,162,75,0.5)"; b.lineWidth = 1;
        b.strokeRect(tx - rw / 2 + 2.5, y - 5.5, rw - 5, 11);
      }
      // vignette
      const vg = b.createRadialGradient(W / 2, H * 0.45, 120, W / 2, H * 0.5, 430);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(4,12,8,0.5)");
      b.fillStyle = vg; b.fillRect(0, 0, W, H);
    }

    /* ---- input: ét tap låser den aktive fase ---- */
    const lock = (e) => {
      audioRef.current.unlock();
      e.preventDefault();
      if (g.over || g.freeze > 0 || g.swingT >= 0) return;
      const key = PHASES[g.idx];
      g.locked[key] = g.u;
      const s = Math.round(zoneScore(g.u, TUNE[key]) * WEIGHTS[key]);
      g.scores[key] = s;
      audioRef.current.lock();
      g.popups.push({ txt: "+" + s, x: 150, y: 190, t: 0, c: s > WEIGHTS[key] * 0.8 ? "#7ae08a" : "#F6EED9" });
      if (g.idx < PHASES.length - 1) {
        g.idx += 1;
        g.t = 0;
        g.freeze = 0.55;
      } else {
        g.total = PHASES.reduce((sum, k) => sum + (g.scores[k] || 0), 0);
        g.bell = g.total >= BELL_SCORE;
        g.swingT = 0;
        audioRef.current.whoosh();
      }
    };
    canvas.addEventListener("mousedown", lock);
    canvas.addEventListener("touchstart", lock, { passive: false });

    const finish = () => {
      if (g.over) return;
      g.over = true;
      audioRef.current.win();
      const total = g.total;
      submitHammerScore(total);       // alle scores tæller (fire-and-forget)
      const size = prizeFor(total);
      setTimeout(() => {
        setResult({ scores: { ...g.scores }, total, bell: g.bell, size });
        if (size) setPicking(true);      // gevinst -> DIREKTE til bamsevalget
        setBest((b2) => {
          const nb = Math.max(b2, total);
          if (nb !== b2) saveBest(nb);
          return nb;
        });
        setPhase("result");
      }, 700);
    };

    /* ---- step ---- */
    const step = (dt) => {
      g.now += dt;
      if (g.freeze > 0) g.freeze -= dt;
      else if (g.swingT < 0 && !g.over) {
        g.t += dt;
        const key = PHASES[g.idx];
        g.u = 0.5 + 0.5 * Math.sin(g.t * TUNE[key].freq * Math.PI - Math.PI / 2);
      }

      if (g.swingT >= 0 && !g.over) {
        g.swingT += dt;
        if (g.swingT >= SWING_T && !g.impactDone) {
          g.impactDone = true;
          audioRef.current.thud(g.total / 1000);
          audioRef.current.rise();
          g.puckVis = true;
          g.puckPhase = "up";
          g.puckT = 0;
          g.litMax = 0;
          g.pulses.fill(0);
          for (let i = 0; i < 10; i++) {
            const a = Math.random() * Math.PI - Math.PI / 2;
            g.parts.push({
              x: TOWER.x, y: TOWER.padY, vx: Math.cos(a) * 90, vy: -Math.abs(Math.sin(a)) * 130 - 40,
              rot: Math.random() * 6, vr: (Math.random() - 0.5) * 9, type: "spark", t: 0, life: 0.5,
            });
          }
        }
        if (g.puckPhase === "up") {
          g.puckT += dt;
          const frac = Math.min(g.total / 1000, 1);
          const targetY = TOWER.padY - 8 - frac * (TOWER.padY - 8 - (BELL_Y + 18));
          const k = Math.min(g.puckT / 0.85, 1);
          const e = 1 - (1 - k) * (1 - k);          // ease-out
          g.puckY = (TOWER.padY - 8) + (targetY - (TOWER.padY - 8)) * e;
          if (k >= 1) {
            if (g.bell) {
              g.puckPhase = "done";
              g.bellWob = 1;
              audioRef.current.bell();
              g.popups.push({ txt: t("hm_bell"), x: W / 2, y: 150, t: 0, c: "#ffd75e", big: true });
              for (let i = 0; i < 26; i++) {
                g.parts.push({
                  x: Math.random() * W, y: -8, vx: (Math.random() - 0.5) * 40, vy: 60 + Math.random() * 80,
                  rot: Math.random() * 6, vr: (Math.random() - 0.5) * 8, type: "confetti", t: 0, life: 2.2,
                });
              }
              setTimeout(finish, 1500);
            } else {
              g.puckPhase = "fall";
              g.puckT = 0;
            }
          }
        } else if (g.puckPhase === "fall") {
          g.puckT += dt;
          g.puckY += 380 * g.puckT * dt * 4;
          if (g.puckY >= TOWER.padY - 8) {
            g.puckY = TOWER.padY - 8;
            g.puckPhase = "done";
            setTimeout(finish, 500);
          }
        }
      }
      if (g.bellWob > 0) g.bellWob = Math.max(0, g.bellWob - dt * 1.4);

      // segmenterne TÆNDER i takt med puckens stigning (tik m. stigende tone)
      if (g.puckVis && g.puckPhase === "up") {
        let lit = 0;
        for (let s = 0; s < 12; s++) if (segYAt(s) >= g.puckY - 2) lit = s + 1;
        while (g.litMax < lit) {
          audioRef.current.tick(g.litMax);
          g.pulses[g.litMax] = 0.4;
          g.litMax++;
        }
      }
      for (let s = 0; s < 12; s++) if (g.pulses[s] > 0) g.pulses[s] = Math.max(0, g.pulses[s] - dt);

      g.parts.forEach((p) => {
        p.t += dt;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.vy += (p.type === "confetti" ? 60 : 320) * dt;
        p.rot += p.vr * dt;
      });
      g.parts = g.parts.filter((p) => p.t < p.life && p.y < H + 20);
      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < (p.big ? 1.7 : 1));
    };

    /* ---- tegning ---- */
    const rr = (c, x, y, w2, h2, r) => {
      c.beginPath();
      c.moveTo(x + r, y);
      c.arcTo(x + w2, y, x + w2, y + h2, r);
      c.arcTo(x + w2, y + h2, x, y + h2, r);
      c.arcTo(x, y + h2, x, y, r);
      c.arcTo(x, y, x + w2, y, r);
      c.closePath();
    };

    /* kuppelklokke m. midterknop + slagarm (Gemini-mockup) */
    const drawBell = () => {
      const wob = Math.sin(g.now * 22) * 0.2 * g.bellWob;
      ctx.save();
      ctx.translate(TOWER.x, BELL_Y);
      ctx.rotate(wob);
      if (g.bellWob > 0) {
        const gl = ctx.createRadialGradient(0, 0, 4, 0, 0, 46);
        gl.addColorStop(0, `rgba(255,225,120,${0.55 * g.bellWob})`);
        gl.addColorStop(1, "rgba(255,225,120,0)");
        ctx.fillStyle = gl;
        ctx.beginPath(); ctx.arc(0, 0, 46, 0, Math.PI * 2); ctx.fill();
      }
      // kuppel
      const bell = ctx.createRadialGradient(-7, -9, 3, 0, -2, 24);
      bell.addColorStop(0, "#fff3c2"); bell.addColorStop(0.4, "#e8c15a"); bell.addColorStop(1, "#9a6f10");
      ctx.fillStyle = bell;
      ctx.beginPath();
      ctx.arc(0, 0, 19, Math.PI, 0);
      ctx.quadraticCurveTo(20, 8, 22, 11);
      ctx.lineTo(-22, 11);
      ctx.quadraticCurveTo(-20, 8, -19, 0);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#7a5510"; ctx.lineWidth = 1.6; ctx.stroke();
      // kant-linje + midterknop
      ctx.strokeStyle = "rgba(122,85,16,0.5)";
      ctx.beginPath(); ctx.moveTo(-20, 7); ctx.lineTo(20, 7); ctx.stroke();
      const knob = ctx.createRadialGradient(-1.5, -13, 1, 0, -12, 5);
      knob.addColorStop(0, "#fff3c2"); knob.addColorStop(1, "#a87914");
      ctx.fillStyle = knob;
      ctx.beginPath(); ctx.arc(0, -12, 4.2, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#7a5510"; ctx.stroke();
      // slagarm t.h. m. kugle (som mockuppets lille arm)
      ctx.strokeStyle = "#8a8f96"; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(21, 2); ctx.lineTo(31, -4); ctx.stroke();
      ctx.fillStyle = "#b9c0c9";
      ctx.beginPath(); ctx.arc(32.5, -5.5, 4, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#5f6a74"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(32.5, -5.5, 4, 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
      ctx.restore();
    };

    /* hammer tegnet i haanden (fallback når pose-renders mangler) */
    const drawMallet = (cx, cy, ang) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(ang);
      ctx.strokeStyle = "#8a5f36"; ctx.lineWidth = 7; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -58); ctx.stroke();
      const hg = ctx.createLinearGradient(-20, -76, 20, -60);
      hg.addColorStop(0, "#a9713d"); hg.addColorStop(0.5, "#7a4a24"); hg.addColorStop(1, "#5a3416");
      ctx.fillStyle = hg;
      rr(ctx, -24, -80, 48, 26, 9); ctx.fill();
      ctx.strokeStyle = "#3a2412"; ctx.lineWidth = 2;
      rr(ctx, -24, -80, 48, 26, 9); ctx.stroke();
      ctx.fillStyle = "#c9a24b";
      ctx.fillRect(-24, -70, 48, 2.4);
      ctx.lineWidth = 1;
      ctx.restore();
    };

    const drawTeddy = () => {
      const pose = currentPose();
      const hasPose = poseReady(pose);
      const th = hasPose ? 150 : 116;
      let cx = 120, cy = FLOOR - th / 2 + Math.sin(g.now * 3) * 1.6;
      let rot = 0;
      /* UDFALDET (Daniels detalje): under svinget lunger bamsen frem mod
         tårnet, så hammerhovedet faktisk RAMMER slagpladen — og træder
         tilbage igen bagefter */
      const LUNGE = 86;                       // frem til pladen
      /* strike-posen HAR hammeren i nedsving — kun let ekstra rotation;
         aim-fallbacken skal selv roteres ind i slaget */
      const swingRot = poseReady("strike") ? 0.18 : 0.6;
      if (g.swingT >= 0) {
        if (g.swingT < SWING_T) {
          const e = (g.swingT / SWING_T) ** 2;   // accelererende udfald
          cx += e * LUNGE;
          cy += e * 7;                        // lille dyk i knæene
          rot = e * swingRot;
        } else if (g.swingT < SWING_T + HOLD_T) {
          cx += LUNGE;                        // HOLD: stå i slaget ved pladen
          cy += 7;
          rot = swingRot;
        } else {
          const back = Math.min((g.swingT - SWING_T - HOLD_T) / 0.45, 1);
          cx += (1 - back) * LUNGE;
          cy += (1 - back) * 7;
          rot = (1 - back) * swingRot;
        }
      }
      if (g.bell && g.swingT >= SWING_T + HOLD_T) {
        const jump = Math.abs(Math.sin(g.now * 5)) * 20;
        ctx.save(); ctx.translate(0, -jump);
      } else ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      if (hasPose) {
        const im = poses[pose];
        const tw = th * (im.naturalWidth / im.naturalHeight);
        ctx.drawImage(im, -tw / 2, -th / 2, tw, th);
      } else if (teddy.complete && teddy.naturalWidth) {
        const tw = 98;
        ctx.drawImage(teddy, -tw / 2, -th / 2, tw, th);
      } else {
        ctx.fillStyle = "#a97b4f";
        ctx.beginPath(); ctx.arc(0, -26, 22, 0, Math.PI * 2); ctx.fill();
        rr(ctx, -19, -8, 38, 54, 13); ctx.fill();
      }
      ctx.restore();
      // fallback-hammer: vinkel efter fase/sving (poserne HAR hammeren med)
      if (!hasPose) {
        let ang = -0.6;                    // hviler over skulderen
        if (g.swingT < 0) {
          const key = PHASES[g.idx];
          const pu = g.locked.pull !== undefined ? g.locked.pull : (key === "pull" ? g.u : 0);
          ang = -0.6 - pu * 1.7;           // løftes op/bagover
        } else if (g.swingT < SWING_T) {
          ang = -2.3 + (g.swingT / SWING_T) * 3.4;   // svinget ned mod pladen
        } else {
          ang = 0.9;
        }
        drawMallet(cx + 26, cy + 6, ang);
      }
    };

    const drawMeter = () => {
      if (g.swingT >= 0 || g.over) return;
      const key = PHASES[g.idx];
      const tune = TUNE[key];
      const mx = 26, mw = W - 52, my = H - 40;
      // guld-dobbeltramme (mockup-stil)
      ctx.fillStyle = "rgba(30,15,5,0.75)";
      rr(ctx, mx - 5, my - 5, mw + 10, 26, 13); ctx.fill();
      ctx.strokeStyle = "#e8c15a"; ctx.lineWidth = 2.4;
      rr(ctx, mx - 5, my - 5, mw + 10, 26, 13); ctx.stroke();
      ctx.strokeStyle = "rgba(122,85,16,0.8)"; ctx.lineWidth = 1;
      rr(ctx, mx - 2.5, my - 2.5, mw + 5, 21, 10); ctx.stroke();
      /* FARVEINDIKATOR: rød bund, orange bånd (tolerancen), grønt felt (bedst) */
      ctx.save();
      rr(ctx, mx, my, mw, 16, 8); ctx.clip();
      ctx.fillStyle = "#8a2a20";
      ctx.fillRect(mx, my, mw, 16);
      const oX = (tune.perfect - tune.tol) * mw, oW = tune.tol * 2 * mw;
      ctx.fillStyle = "#e08a2e";
      ctx.fillRect(mx + oX, my, oW, 16);
      const gX = (tune.perfect - tune.tol * 0.42) * mw, gW = tune.tol * 0.84 * mw;
      ctx.fillStyle = "#3fae46";
      ctx.fillRect(mx + gX, my, gW, 16);
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fillRect(mx, my, mw, 5);
      ctx.restore();
      // perfekt-streg + viser
      ctx.fillStyle = "#fff";
      ctx.fillRect(mx + tune.perfect * mw - 1.5, my - 6, 3, 28);
      const vx = mx + g.u * mw;
      ctx.beginPath();
      ctx.moveTo(vx - 7, my - 10); ctx.lineTo(vx + 7, my - 10); ctx.lineTo(vx, my - 1);
      ctx.closePath(); ctx.fill();
      // fase-instruktion
      ctx.textAlign = "center";
      ctx.font = "600 14px Georgia, 'Times New Roman', serif";
      ctx.lineWidth = 3; ctx.strokeStyle = "rgba(8,20,14,0.7)";
      const txt = t("hm_hint_" + key);
      ctx.strokeText(txt, W / 2, my - 14, W - 40);
      ctx.fillStyle = "#e7c469";
      ctx.fillText(txt, W / 2, my - 14, W - 40);
      ctx.lineWidth = 1;
      ctx.font = "800 12px system-ui, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.fillText((g.idx + 1) + " / 3", W / 2, my + 30);
    };

    const draw = () => {
      ctx.drawImage(bg, 0, 0, W, H);
      // GLØD-overlay: segmenterne lyser op i takt med pucken (og slukker på vej ned)
      if (g.puckVis) {
        const rw = TOWER.railW;
        for (let s = 0; s < 12; s++) {
          const lit = (g.puckPhase === "done" && g.bell) || segYAt(s) >= g.puckY - 2;
          if (!lit) continue;
          const y = segYAt(s);
          const [hi, lo] = segGlow(s);
          const pulse = g.pulses[s] > 0 ? g.pulses[s] / 0.4 : 0;
          ctx.save();
          ctx.shadowColor = hi;
          ctx.shadowBlur = 8 + pulse * 10;
          const seg = ctx.createLinearGradient(0, y - 6, 0, y + 6);
          seg.addColorStop(0, hi); seg.addColorStop(1, lo);
          ctx.fillStyle = seg;
          rr(ctx, TOWER.x - rw / 2 + 2.5, y - 5.5, rw - 5, 11, 3);
          ctx.fill();
          ctx.shadowBlur = 0;
          ctx.fillStyle = `rgba(255,255,255,${0.5 + pulse * 0.4})`;
          ctx.fillRect(TOWER.x - rw / 2 + 4, y - 4.5, rw - 8, 2);
          if (pulse > 0) {
            ctx.globalAlpha = pulse * 0.5;
            ctx.strokeStyle = "#fff3c8"; ctx.lineWidth = 2;
            rr(ctx, TOWER.x - rw / 2 + 0.5, y - 7.5, rw - 1, 15, 4);
            ctx.stroke();
            ctx.globalAlpha = 1; ctx.lineWidth = 1;
          }
          ctx.restore();
        }
      }
      drawBell();
      // pucken
      if (g.puckVis) {
        const pg = ctx.createLinearGradient(TOWER.x - 10, 0, TOWER.x + 10, 0);
        pg.addColorStop(0, "#f0d27a"); pg.addColorStop(1, "#b8862a");
        ctx.fillStyle = pg;
        rr(ctx, TOWER.x - 11, g.puckY - 5, 22, 10, 5); ctx.fill();
        ctx.strokeStyle = "#7a5510"; ctx.stroke();
      }
      drawTeddy();
      // fase-chips: trae-plaketter m. moerk smedejerns-ramme (mockup-stil)
      ctx.textAlign = "left";
      PHASES.forEach((k, i) => {
        const cxp = 12 + i * 90;
        const done = g.scores[k] !== undefined;
        // jern-ramme (moerk ydre)
        rr(ctx, cxp - 2, 22, 88, 32, 10);
        ctx.fillStyle = "#1c2024"; ctx.fill();
        // trae-plade
        const cg = ctx.createLinearGradient(0, 24, 0, 52);
        cg.addColorStop(0, done ? "#8a5a2e" : "#6b4526");
        cg.addColorStop(1, done ? "#5f3d1f" : "#42280f");
        rr(ctx, cxp, 24, 84, 28, 8);
        ctx.fillStyle = cg; ctx.fill();
        ctx.strokeStyle = done ? "#e8c15a" : "rgba(201,162,75,0.45)"; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.font = "800 10px system-ui, sans-serif";
        ctx.fillStyle = done ? "#ffe9a8" : "rgba(246,238,217,0.65)";
        ctx.fillText(t("hm_ph_" + k), cxp + 8, 36);
        ctx.font = "800 12px system-ui, sans-serif";
        ctx.fillStyle = done ? "#7ae08a" : "rgba(255,255,255,0.35)";
        ctx.fillText(done ? g.scores[k] + "" : "· · ·", cxp + 8, 48);
        ctx.lineWidth = 1;
      });
      drawMeter();
      // partikler
      for (const p of g.parts) {
        const a = Math.max(0, 1 - p.t / p.life);
        ctx.globalAlpha = a;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.type === "spark") {
          ctx.fillStyle = "#ffd75e";
          ctx.shadowColor = "#ffd75e"; ctx.shadowBlur = 8;
          ctx.beginPath(); ctx.arc(0, 0, 2.6, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = ["#e5563f", "#ffd75e", "#5fc0e8", "#7acb5f"][Math.floor(p.rot * 7) % 4];
          ctx.fillRect(-4, -2, 8, 4);
        }
        ctx.restore();
        ctx.globalAlpha = 1;
      }
      // popups
      for (const p of g.popups) {
        const life = p.big ? 1.7 : 1;
        ctx.globalAlpha = 1 - p.t / life;
        ctx.font = p.big ? "bold 30px 'Alfa Slab One', Georgia, serif" : "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(8,20,14,0.8)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 26);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 26);
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (ts) => {
      const dt = Math.min((ts - last) / 1000, 0.033);
      last = ts;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", lock);
      canvas.removeEventListener("touchstart", lock);
    };
  }, [phase]);

  /* ================= UI ================= */
  return (
    <div className={"hm-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_hammer").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("hm_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          {chal && (
            <div className="hm-chal">{t("pb_ch_banner", { name: chal.by, n: chal.ch })}</div>
          )}
          <p className="lede">{t("hm_lede")}</p>
          <ul className="rules">
            <li>{t("hm_rule1")}</li>
            <li>{t("hm_rule2")}</li>
            <li>{t("hm_rule3")}</li>
          </ul>
          <p className="prizenote">{t("hm_prizenote")}{best > 0 ? " · " + t("hm_best", { n: best }) : ""}</p>
          <button className="cta" onClick={startGame}>{t("hm_start")}</button>
          <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("hm_best", { n: best })}</span>
            {chal && <span className="last">{chal.by}: {chal.ch}</span>}
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "result" && result && !picking && (
        <div className="overlay">
          <div className="card">
            <div className={"ribbon" + (result.total >= 750 ? "" : " dark")}>
              {result.bell ? t("hm_bell") : result.total >= 750 ? t("hm_res_great") : result.total >= 500 ? t("hm_res_ok") : t("hm_res_low")}
            </div>
            <p className="hm-total">{result.total}<span> / 1000</span></p>
            <div className="hm-break">
              {PHASES.map((k) => (
                <div key={k} className="hm-row">
                  <span>{t("hm_ph_" + k)}</span>
                  <b>{result.scores[k] || 0} / {WEIGHTS[k]}</b>
                </div>
              ))}
            </div>
            {chal && (
              <p className="hm-chresult">
                {result.total > chal.ch ? t("pb_ch_beat", { name: chal.by }) : t("pb_ch_lost", { name: chal.by, n: chal.ch })}
              </p>
            )}
            <button className="cta gold" onClick={() => share(result.total)}>
              {copied ? t("pb_copied") : t("pb_challenge")}
            </button>
            <button className="cta hs" onClick={openHs}>{t("pb_hs_btn")}</button>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {picking && result && result.size && (
        <PrizePicker
          size={result.size}
          game={t("tile_hammer")}
          onDone={() => setPicking(false)}
        />
      )}

      {hsOpen && (() => {
        const list = hsData ? (hsTab === "month" ? hsData.month : hsData.alltime) : [];
        return (
          <div className="overlay hsov" onClick={(e) => { if (e.target === e.currentTarget) setHsOpen(false); }}>
            <div className="card hscard">
              <div className="ribbon">{t("pb_hs_btn")}</div>
              <div className="hstabs">
                <button className={hsTab === "month" ? "on" : ""} onClick={() => setHsTab("month")}>{t("pb_hs_month")}</button>
                <button className={hsTab === "all" ? "on" : ""} onClick={() => setHsTab("all")}>{t("pb_hs_all")}</button>
              </div>
              {!hsData && !hsErr && <p className="hsmsg">· · ·</p>}
              {hsErr && <p className="hsmsg">{t("pb_hs_err")}</p>}
              {hsData && (list.length ? (
                <div className="hslist">
                  {list.map((e, i) => (
                    <div key={e.dev + i} className={"hsrow" + (e.me ? " me" : "")}>
                      <span className="rk">{i + 1}</span>
                      <span className="nm">{e.name}<em>{fmtDate(e.ts, hsTab === "all")}</em></span>
                      <b>{e.score}</b>
                    </div>
                  ))}
                </div>
              ) : <p className="hsmsg">{t("pb_hs_empty")}</p>)}
              <button className="cta alt" onClick={() => setHsOpen(false)}>{t("pb_hs_close")}</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const css = `
.hm-root{
  --cream:#F6EED9; --ink:#22301c; --red:#C8352E;
  min-height:100vh; background:#0b1e18;
  background-image:radial-gradient(circle at 50% -10%, #1e4436 0%, #0b1e18 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #1e4534, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.cta{
  width:100%; margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#3f6a54; box-shadow:0 4px 0 #244536; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #244536;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.hm-chal{
  background:linear-gradient(#f7df9a,#e9b94e); color:#4a2c00;
  border:2px solid #a87914; border-radius:10px;
  font-weight:800; font-size:14px; padding:10px 12px; margin:0 0 12px;
}
.hm-chresult{font-weight:800; font-size:14px; margin:0 0 4px; color:#7e1f1a;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.scorebar .last{color:#7e1f1a;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #35543f; box-shadow:0 12px 30px rgba(0,0,0,.55);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(6,14,10,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:21px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#3f6a54;}
.hm-total{
  font-family:'Alfa Slab One',Georgia,serif; font-size:46px; margin:2px 0 10px; color:#7e1f1a;
}
.hm-total span{font-size:18px; color:#8a7a55;}
.hm-break{margin:0 0 12px;}
.hm-row{
  display:flex; justify-content:space-between; font-weight:800; font-size:13.5px;
  padding:5px 2px; border-bottom:1px dashed #cbbf9c;
}
.hm-row:last-child{border-bottom:none;}
.cta.hs{
  background:linear-gradient(180deg,#7a4e2c,#5f3d1f); color:#ffe9a8;
  border:1.5px solid #c9a24b; box-shadow:0 4px 0 #3f2812; font-size:15px; padding:11px;
}
.cta.hs:active{box-shadow:0 1px 0 #3f2812;}
.hsov{z-index:12;}
.hscard{max-height:86vh; overflow-y:auto;}
.hstabs{display:flex; gap:8px; margin:0 0 10px;}
.hstabs button{
  flex:1; padding:9px 6px; font-family:inherit; font-weight:800; font-size:13.5px;
  border-radius:8px; cursor:pointer; border:1.5px solid #cbbf9c; background:#efe6cd; color:#6b5c38;
}
.hstabs button.on{background:linear-gradient(180deg,#f7df9a,#e9b94e); border-color:#a87914; color:#4a2c00;}
.hslist{margin:0 0 6px;}
.hsrow{
  display:flex; align-items:center; gap:10px; font-weight:800; font-size:14px;
  padding:6px 8px; border-bottom:1px dashed #cbbf9c; border-radius:6px;
}
.hsrow:last-child{border-bottom:none;}
.hsrow .rk{width:20px; text-align:right; color:#8a7a55;}
.hsrow .nm{flex:1; text-align:left; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;}
.hsrow .nm em{font-style:normal; font-weight:700; font-size:11px; color:#8a7a55; margin-left:8px;}
.hsrow b{color:#7e1f1a; font-size:15px;}
.hsrow.me{background:linear-gradient(180deg,#f9ecc4,#f2dc9a); box-shadow:inset 0 0 0 1.5px #c9a24b;}
.hsmsg{font-weight:800; font-size:14px; color:#8a7a55; padding:14px 0;}
`;
